import PropTypes from 'prop-types';
import { Menu, LogOut, User, Wifi, WifiOff, RefreshCw, CloudOff, Languages } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import useOfflineSync from '../../hooks/useOfflineSync';
import useTranslation from '../../hooks/useTranslation';

const NetworkStatusIndicator = ({ isOnline, isSyncing, pendingCount, onSync }) => {
  const { t } = useTranslation();

  if (!isOnline) {
    return (
      <div className="flex items-center px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm">
        <WifiOff className="h-4 w-4 mx-1" />
        <span className="hidden sm:inline">{t('common.offline')}</span>
        {pendingCount > 0 && (
          <span className="mx-1 bg-red-200 text-red-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
            {pendingCount}
          </span>
        )}
      </div>
    );
  }

  if (isSyncing) {
    return (
      <div className="flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm">
        <RefreshCw className="h-4 w-4 mx-1 animate-spin" />
        <span className="hidden sm:inline">{t('common.syncing')}</span>
      </div>
    );
  }

  if (pendingCount > 0) {
    return (
      <button
        onClick={onSync}
        className="flex items-center px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm hover:bg-amber-200 transition-colors"
      >
        <CloudOff className="h-4 w-4 mx-1" />
        <span className="hidden sm:inline">{t('common.pending')}</span>
        <span className="mx-1 bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
          {pendingCount}
        </span>
      </button>
    );
  }

  return (
    <div className="flex items-center px-3 py-1.5 text-green-600 text-sm">
      <Wifi className="h-4 w-4" />
      <span className="hidden sm:inline mx-1">{t('common.online')}</span>
    </div>
  );
};

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLanguage } = useTranslation();
  const { isOnline, isSyncing, pendingCount, triggerSync } = useOfflineSync();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      <div className="flex items-center gap-3">
        <NetworkStatusIndicator
          isOnline={isOnline}
          isSyncing={isSyncing}
          pendingCount={pendingCount}
          onSync={triggerSync}
        />

        <button
          onClick={toggleLanguage}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors border border-gray-200"
          title={t('language')}
        >
          <Languages className="h-4 w-4" />
          <span>{t('language')}</span>
        </button>

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              {user?.profileImage ? (
                <img
                  src={user.profileImage}
                  alt={user.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <User className="h-5 w-5 text-primary-600" />
              )}
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium text-gray-700">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500">{t('operator')}</p>
            </div>
          </button>

          {dropdownOpen && (
            <div className="absolute end-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="h-4 w-4 mx-1" />
                {t('logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  onMenuClick: PropTypes.func.isRequired,
};

export default Header;
